import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { myFetch } from '@commons/fetch';
import { notifications } from '@mantine/notifications';
import { AppDispatch } from '../index';

// Types
interface FiltersState {
  filters: any[];
  options: any[];
  fields: any[];
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;
}

const initialState: FiltersState = {
  filters: [],
  options: [],
  fields: [],
  isLoading: false,
  error: null,
  hasFetched: false,
};

// Async Thunks
export const fetchFilters = createAsyncThunk(
  'filters/fetchFilters',
  async (username: string, { rejectWithValue }) => {
    try {
      const response = await myFetch(`/${username}/filters`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch filters');
    }
  }
);

export const fetchFilter = createAsyncThunk(
  'filters/fetchFilter',
  async (
    { username, filterId }: { username: string; filterId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await myFetch(`/${username}/filters/${filterId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch filter');
    }
  }
);

export const fetchOptions = createAsyncThunk(
  'filters/fetchOptions',
  async (username: string, { rejectWithValue }) => {
    try {
      const response = await myFetch(`/${username}/models/options`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch options');
    }
  }
);

export const fetchFields = createAsyncThunk(
  'filters/fetchFields',
  async (
    { username, category }: { username: string; category: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await myFetch(
        `/${username}/models/fields?category=${category}`
      );
      return response.data || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch fields');
    }
  }
);

export const addFilter = createAsyncThunk(
  'filters/addFilter',
  async (
    { username, filter }: { username: string; filter: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await myFetch(`/${username}/filters`, {
        method: 'POST',
        body: JSON.stringify(filter),
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add filter');
    }
  }
);

export const editFilter = createAsyncThunk(
  'filters/editFilter',
  async (
    { username, filter }: { username: string; filter: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await myFetch(`/${username}/filters/${filter.id}`, {
        method: 'PUT',
        body: JSON.stringify(filter),
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to edit filter');
    }
  }
);

export const deleteFilter = createAsyncThunk(
  'filters/deleteFilter',
  async (
    { username, filterId }: { username: string; filterId: string },
    { rejectWithValue }
  ) => {
    try {
      await myFetch(`/${username}/filters/${filterId}`, {
        method: 'DELETE',
      });
      return filterId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete filter');
    }
  }
);

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setFields: (state, action: PayloadAction<any[]>) => {
      state.fields = action.payload;
    },
    clearFields: (state) => {
      state.fields = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    clearFilters: (state) => {
      state.filters = [];
      state.error = null;
      state.hasFetched = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch Filters
    builder
      .addCase(fetchFilters.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFilters.fulfilled, (state, action) => {
        state.isLoading = false;
        state.filters = action.payload;
        state.hasFetched = true;
      })
      .addCase(fetchFilters.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.hasFetched = true;
      });

    // Fetch Filter
    builder.addCase(fetchFilter.rejected, (state, action) => {
      state.error = action.payload as string;
    });

    // Fetch Options
    builder
      .addCase(fetchOptions.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchOptions.fulfilled, (state, action) => {
        state.options = action.payload;
      })
      .addCase(fetchOptions.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Fetch Fields
    builder
      .addCase(fetchFields.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchFields.fulfilled, (state, action) => {
        state.fields = action.payload;
      })
      .addCase(fetchFields.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Add Filter
    builder
      .addCase(addFilter.fulfilled, (state, action) => {
        const newFilter = action.payload;
        state.filters = [...state.filters, newFilter];
        state.fields = [];
        // Initialize filter state in localStorage
        const filterStateKey = `userFoldersState_${newFilter.id}`;
        const defaultState = {
          currentPage: 1,
          itemsPerPage: 10,
          filters: {
            isArchived: false,
            onlyWithData: false,
            backOfficeStatus: '',
            folderName: '',
            fileName: '',
            isPublished: '',
            isSigned: '',
            isLocked: '',
            fileStatus: '',
          },
        };
        localStorage.setItem(filterStateKey, JSON.stringify(defaultState));
      })
      .addCase(addFilter.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Edit Filter
    builder
      .addCase(editFilter.fulfilled, (state, action) => {
        const updatedFilter = action.payload;
        const oldFilter = state.filters.find((f) => f.id === updatedFilter.id);

        state.filters = state.filters.map((f) =>
          f.id === updatedFilter.id ? updatedFilter : f
        );
        state.fields = [];

        // Update filter state in localStorage
        const filterStateKey = `userFoldersState_${updatedFilter.id}`;
        const existingState = localStorage.getItem(filterStateKey);

        if (existingState) {
          try {
            const savedState = JSON.parse(existingState);
            const savedFilters = savedState.filters || {};

            // Get field names from old and new filter
            const oldFieldNames = oldFilter?.fields
              ? oldFilter.fields.map((f: any) =>
                  typeof f === 'string' ? f : f.name
                )
              : [];
            const newFieldNames = updatedFilter.fields
              ? updatedFilter.fields.map((f: any) =>
                  typeof f === 'string' ? f : f.name
                )
              : [];

            // Find fields that were removed
            const removedFields = oldFieldNames.filter(
              (name: string) => !newFieldNames.includes(name)
            );

            // Remove filter values for fields that no longer exist
            const cleanedFilters = { ...savedFilters };
            removedFields.forEach((fieldName: string) => {
              delete cleanedFilters[fieldName];
            });

            // Update localStorage with cleaned filters
            const updatedState = {
              ...savedState,
              filters: cleanedFilters,
            };
            localStorage.setItem(filterStateKey, JSON.stringify(updatedState));
          } catch (error) {
            // If parsing fails, reinitialize
            const defaultState = {
              currentPage: 1,
              itemsPerPage: 10,
              filters: {
                isArchived: false,
                onlyWithData: false,
                backOfficeStatus: '',
                folderName: '',
                fileName: '',
                isPublished: '',
                isSigned: '',
                isLocked: '',
                fileStatus: '',
              },
            };
            localStorage.setItem(filterStateKey, JSON.stringify(defaultState));
          }
        } else {
          // Initialize if it doesn't exist
          const defaultState = {
            currentPage: 1,
            itemsPerPage: 10,
            filters: {
              isArchived: false,
              onlyWithData: false,
              backOfficeStatus: '',
              folderName: '',
              fileName: '',
              isPublished: '',
              isSigned: '',
              isLocked: '',
              fileStatus: '',
            },
          };
          localStorage.setItem(filterStateKey, JSON.stringify(defaultState));
        }
      })
      .addCase(editFilter.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete Filter
    builder
      .addCase(deleteFilter.fulfilled, (state, action) => {
        const filterId = action.payload;
        state.filters = state.filters.filter((f) => f.id !== filterId);
        state.fields = [];
        // Clear filter state from localStorage
        const filterStateKey = `userFoldersState_${filterId}`;
        localStorage.removeItem(filterStateKey);
      })
      .addCase(deleteFilter.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { setFields, clearFields, clearError, clearFilters } =
  filtersSlice.actions;

// Thunk creators with notifications
export const fetchFiltersWithNotification =
  (username: string) => async (dispatch: AppDispatch) => {
    try {
      await dispatch(fetchFilters(username)).unwrap();
    } catch (error: any) {
      notifications.show({
        color: 'red',
        message: 'Failed to fetch filters',
        autoClose: 3000,
      });
    }
  };

export const fetchFilterWithNotification =
  (username: string, filterId: string, callback?: (data: any) => void) =>
  async (dispatch: AppDispatch) => {
    try {
      const result = await dispatch(
        fetchFilter({ username, filterId })
      ).unwrap();
      if (callback) {
        callback(result);
      }
    } catch (error: any) {
      notifications.show({
        color: 'red',
        message: 'Failed to fetch filter',
        autoClose: 3000,
      });
    }
  };

export const fetchOptionsWithNotification =
  (username: string) => async (dispatch: AppDispatch) => {
    try {
      await dispatch(fetchOptions(username)).unwrap();
    } catch (error: any) {
      notifications.show({
        color: 'red',
        message: 'Failed to fetch options',
        autoClose: 3000,
      });
    }
  };

export const fetchFieldsWithNotification =
  (username: string, category: string) => async (dispatch: AppDispatch) => {
    try {
      await dispatch(fetchFields({ username, category })).unwrap();
    } catch (error: any) {
      notifications.show({
        color: 'red',
        message: 'Failed to fetch fields',
        autoClose: 3000,
      });
    }
  };

export const addFilterWithNotification =
  (username: string, filter: any, callback?: () => void) =>
  async (dispatch: AppDispatch) => {
    try {
      await dispatch(addFilter({ username, filter })).unwrap();
      if (callback) {
        callback();
      }
      notifications.show({
        color: 'green',
        message: 'Filter added successfully',
        autoClose: 3000,
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        message: 'Failed to add filter',
        autoClose: 3000,
      });
    }
  };

export const editFilterWithNotification =
  (username: string, filter: any, callback?: () => void) =>
  async (dispatch: AppDispatch) => {
    try {
      await dispatch(editFilter({ username, filter })).unwrap();
      if (callback) {
        callback();
      }
      localStorage.removeItem(filter.id);
      notifications.show({
        color: 'green',
        message: 'Filter updated successfully',
        autoClose: 3000,
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        message: 'Failed to update filter',
        autoClose: 3000,
      });
    }
  };

export const deleteFilterWithNotification =
  (username: string, filter: any) => async (dispatch: AppDispatch) => {
    try {
      await dispatch(deleteFilter({ username, filterId: filter.id })).unwrap();
      // Clear filter state from localStorage
      const filterStateKey = `userFoldersState_${filter.id}`;
      localStorage.removeItem(filterStateKey);
      notifications.show({
        color: 'green',
        message: 'Filter deleted successfully',
        autoClose: 3000,
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        message: error.message || 'Failed to delete filter',
        autoClose: 3000,
      });
    }
  };

export default filtersSlice.reducer;
